import { AccessKey } from './access-control.config';

export interface SidebarMenuItem {
  label: string;
  icon: string;
  routerLink?: string;
  dynamicRouterLink?: (userId: number | null) => string;
  accessKey: AccessKey;
  dropdownKey?: string;
  children?: SidebarMenuItem[];
}

export interface SidebarMenuSection {
  header: string;
  items: SidebarMenuItem[];
}

export const SIDEBAR_MENU: SidebarMenuSection[] = [
  {
    header: 'HOME',
    items: [
      {
        label: 'Dashboard',
        icon: 'bi bi-ui-checks-grid',
        routerLink: '/app',
        accessKey: 'HOME'
      },
      {
        label: 'File Activity Log',
        icon: 'bi bi-clock-history',
        routerLink: '/app/admin/file-manager-audit',
        accessKey: 'FILE_MANAGER_AUDIT'
      }
    ]
  },
  {
    header: 'CLIENTS',
    items: [
      {
        label: 'List',
        icon: 'bi bi-list-task section-completed',
        routerLink: '/app/clients',
        accessKey: 'CLIENTS_LIST'
      },
      {
        label: 'Details',
        icon: 'bi bi-person-vcard-fill',
        dynamicRouterLink: (userId) => (userId ? `/app/clients/${userId}` : '/app/clients'),
        accessKey: 'CLIENTS_DETAILS'
      },
      {
        label: 'Settings',
        icon: 'bi bi-tools',
        accessKey: 'CLIENTS_SETTINGS',
        dropdownKey: 'settings',
        children: [
          {
            label: 'Message',
            icon: 'bi bi-ui-checks-grid section-completed',
            routerLink: '/app/clients/settings/message',
            accessKey: 'CLIENTS_SETTINGS'
          },
          {
            label: 'Client types',
            icon: 'bi bi-list-task section-completed',
            routerLink: '/app/clients/settings/client-types',
            accessKey: 'CLIENTS_SETTINGS'
          },
          {
            label: 'Documents for new clients',
            icon: 'bi bi-ui-checks-grid',
            routerLink: '/app/clients/settings/documents-for-new-clients',
            accessKey: 'CLIENTS_SETTINGS'
          }
        ]
      }
    ]
  },
  {
    header: 'REQUESTS',
    items: [
      {
        label: 'List',
        icon: 'bi bi-list-task section-completed',
        routerLink: '/app/requests',
        accessKey: 'REQUESTS_LIST'
      },
      {
        label: 'Settings',
        icon: 'bi bi-tools',
        accessKey: 'REQUESTS_SETTINGS',
        dropdownKey: 'requestSettings',
        children: [
          {
            label: 'Request Categories',
            icon: 'bi bi-list-task section-completed',
            routerLink: '/app/requests/settings/request-categories',
            accessKey: 'REQUESTS_SETTINGS'
          },
          {
            label: 'Traffic Lights',
            icon: 'bi bi-stoplights',
            accessKey: 'REQUESTS_TRAFFIC_LIGHTS',
            dropdownKey: 'requestTrafficLights',
            children: [
              {
                label: 'Waiting Time',
                icon: 'bi bi-hourglass-split',
                routerLink: '/app/requests/settings/request-traffic-lights/waiting-time',
                accessKey: 'REQUESTS_TRAFFIC_LIGHTS'
              },
              {
                label: 'Time Zone',
                icon: 'bi bi-clock-history',
                routerLink: '/app/requests/settings/request-traffic-lights/time-zone',
                accessKey: 'REQUESTS_TRAFFIC_LIGHTS'
              }
            ]
          }
        ]
      }
    ]
  },
  {
    header: 'PROPOSALS',
    items: [
      {
        label: 'List',
        icon: 'bi bi-list-task section-completed',
        routerLink: '/app/proposals',
        accessKey: 'PROPOSALS_LIST'
      },
      {
        label: 'Archived',
        icon: 'bi bi-list-task',
        routerLink: '/app/proposals/archived',
        accessKey: 'PROPOSALS_ARCHIVED'
      },
      {
        label: 'Settings',
        icon: 'bi bi-tools',
        accessKey: 'PROPOSALS_SETTINGS',
        dropdownKey: 'proposalsSettings',
        children: [
          {
            label: 'Proposal Steps',
            icon: 'bi bi-list-task',
            routerLink: '/app/proposals/settings/proposal-steps',
            accessKey: 'PROPOSALS_SETTINGS'
          },
          {
            label: 'Payment Terms',
            icon: 'bi bi-list-task',
            routerLink: '/app/proposals/settings/payment-terms',
            accessKey: 'PROPOSALS_SETTINGS'
          }
        ]
      }
    ]
  },
  {
    header: 'PROJECTS',
    items: [
      {
        label: 'List',
        icon: 'bi bi-list-task',
        routerLink: '/app/projects',
        accessKey: 'PROJECTS_LIST'
      },
      {
        label: 'Certificates List',
        icon: 'bi bi-list-task',
        routerLink: '/app/projects/certificates',
        accessKey: 'PROJECTS_CERTIFICATES_LIST'
      },
      {
        label: 'Settings',
        icon: 'bi bi-tools',
        accessKey: 'PROJECTS_SETTINGS',
        dropdownKey: 'projectSettings',
        children: [
          {
            label: 'Checklist',
            icon: 'bi bi-list-task',
            routerLink: '/app/projects/settings/checklist',
            accessKey: 'PROJECTS_SETTINGS'
          },
          {
            label: 'Checklist group',
            icon: 'bi bi-list-task',
            routerLink: '/app/projects/settings/checklist-group',
            accessKey: 'PROJECTS_SETTINGS'
          },
          {
            label: 'Testlist',
            icon: 'bi bi-list-task',
            routerLink: '/app/projects/settings/testlist',
            accessKey: 'PROJECTS_SETTINGS'
          }
        ]
      }
    ]
  },
  {
    header: 'TECH',
    items: [
      {
        label: 'Upload docs/video',
        icon: 'bi bi-upload',
        routerLink: '/app/tech/upload',
        accessKey: 'TECH_UPLOAD_DOCS_VIDEO'
      },
      {
        label: 'File Manager',
        icon: 'bi bi-folder2-open',
        routerLink: '/app/tech/file-manager',
        accessKey: 'TECH_FILE_MANAGER'
      },
    ]
  },
  {
    header: 'SHARED RESOURCES',
    items: [
      {
        label: 'File Manager',
        icon: 'bi bi-folder2-open',
        routerLink: '/app/shared-resources/file-manager',
        accessKey: 'SHARED_RESOURCES_FILE_MANAGER'
      },
    ]
  },
  {
    header: 'IT',
    items: [
      {
        label: 'File Manager',
        icon: 'bi bi-folder2-open',
        routerLink: '/app/it/file-manager',
        accessKey: 'IT_FILE_MANAGER'
      },
      {
        label: 'Antivirus Log',
        icon: 'bi bi-list-task',
        routerLink: '/app/it/antivirus-fileupload-log',
        accessKey: 'IT_ANTIVIRUS_LOG'
      }
    ]
  },
  {
    header: 'COMPANY OPERATION',
    items: [
      {
        label: 'File Manager',
        icon: 'bi bi-folder2-open',
        routerLink: '/app/company-operation/file-manager',
        accessKey: 'COMPANY_OPERATION_FILE_MANAGER'
      }
    ]
  },
  {
    header: 'COMPLIANCE',
    items: [
      {
        label: 'File Manager',
        icon: 'bi bi-folder2-open',
        routerLink: '/app/compliance/file-manager',
        accessKey: 'COMPLIANCE_FILE_MANAGER'
      }
    ]
  },
  {
    header: 'SALES',
    items: [
      {
        label: 'Proposal',
        icon: 'bi bi-copy',
        routerLink: '/app/sales/proposal',
        accessKey: 'SALES_PROPOSAL'
      },
      {
        label: 'Project',
        icon: 'bi bi-file-earmark-ruled',
        routerLink: '/app/sales/project',
        accessKey: 'SALES_PROJECT'
      },
      {
        label: 'To Do List',
        icon: 'bi bi-ui-radios',
        routerLink: '/app/sales/to-do-list',
        accessKey: 'SALES_TO_DO_LIST'
      },
      {
        label: 'Contact Management',
        icon: 'bi bi-sticky-fill',
        routerLink: '/app/sales/contact-management',
        accessKey: 'SALES_CONTACT_MANAGEMENT'
      }, {
        label: 'File Manager',
        icon: 'bi bi-folder2-open',
        routerLink: '/app/sales/file-manager',
        accessKey: 'SALES_FILE_MANAGER'
      }
    ]
  },
  {
    header: 'MANAGEMENT',
    items: [
      {
        label: 'File Manager',
        icon: 'bi bi-folder2-open',
        routerLink: '/app/management/file-manager',
        accessKey: 'MANAGEMENT_FILE_MANAGER'
      }
    ]
  },
  {
    header: 'INTRODUCER',
    items: [
      {
        label: 'List',
        icon: 'bi bi-list-task',
        routerLink: '/app/introducers',
        accessKey: 'INTRODUCER_LIST'
      },
      {
        label: 'Revenue List',
        icon: 'bi bi-cash section-completed',
        routerLink: '/app/introducers/revenue-list',
        accessKey: 'INTRODUCER_REVENUE_LIST'
      },
      {
        label: 'Introducers clients',
        icon: 'bi bi-person-vcard section-completed',
        routerLink: '/app/introducers/clients',
        accessKey: 'INTRODUCER_CLIENTS'
      },
    ]
  },
  {
    header: 'REGULATORS',
    items: [
      {
        label: 'List',
        icon: 'bi bi-list-task section-completed',
        routerLink: '/app/regulators',
        accessKey: 'REGULATORS_LIST'
      }
    ]
  },
  {
    header: 'FINANCE',
    items: [
      {
        label: 'Invoices',
        icon: 'bi bi-receipt',
        routerLink: '/app/finance/invoices',
        accessKey: 'FINANCE_INVOICES'
      },
      {
        label: 'Reports',
        icon: 'bi bi-file-earmark-text',
        routerLink: '/app/finance/reports',
        accessKey: 'FINANCE_REPORTS'
      },
      {
        label: 'File Manager',
        icon: 'bi bi-folder2-open',
        routerLink: '/app/finance/file-manager',
        accessKey: 'FINANCE_FILE_MANAGER'
      },
      {
        label: 'Project Payments Report',
        icon: 'bi bi-calendar-check section-semicompleted',
        routerLink: '/app/finance/project-payments-report',
        accessKey: 'FINANCE_PROJECT_PAYMENTS_REPORT'
      },
    ]
  },
  {
    header: 'HR',
    items: [
      {
        label: 'Personnel',
        icon: 'bi bi-list-task',
        routerLink: '/app/hr/employees',
        accessKey: 'HR_EMPLOYEES'
      },
      {
        label: 'Daily Report',
        icon: 'bi bi-list-task',
        routerLink: '/app/hr/daily-report',
        accessKey: 'HR_DAILY_REPORT'
      },
      {
        label: 'Ranking Staff',
        icon: 'bi bi-copy',
        routerLink: '/app/hr/ranking-staff',
        accessKey: 'HR_RANKING_STAFF'
      },
      {
        label: 'Appraisal',
        icon: 'bi bi-tools',
        accessKey: 'HR_APPRAISAL',
        dropdownKey: 'hrAppraisal',
        children: [
          {
            label: 'List',
            icon: 'bi bi-list-task',
            routerLink: '/app/hr/appraisal/list',
            accessKey: 'HR_APPRAISAL'
          },
          {
            label: 'To Do ...',
            icon: 'bi bi-ui-radios',
            routerLink: '/app/hr/appraisal/to-do',
            accessKey: 'HR_APPRAISAL'
          },
        ]
      },
      {
        label: 'Training',
        icon: 'bi bi-duffle',
        routerLink: '/app/hr/training',
        accessKey: 'HR_TRAINING'
      },
      {
        label: 'Specialisation',
        icon: 'bi bi-mortarboard',
        routerLink: '/app/hr/specialisations',
        accessKey: 'HR_SPECIALISATION'
      },
      {
        label: 'Settings',
        icon: 'bi bi-tools',
        routerLink: '/app/hr/settings',
        accessKey: 'HR_SETTINGS'
      },
      {
        label: 'File Manager',
        icon: 'bi bi-folder2-open',
        routerLink: '/app/hr/file-manager',
        accessKey: 'HR_FILE_MANAGER'
      }
    ]
  },
  {
    header: 'COMPLAINT',
    items: [
      {
        label: 'List',
        icon: 'bi bi-list-task',
        routerLink: '/app/complaints',
        accessKey: 'COMPLAINT_LIST'
      }
    ]
  },
  {
    header: 'QUALITY MANAGEMENT',
    items: [
      {
        label: 'Risk Management',
        icon: 'bi bi-coin section-semicompleted',
        accessKey: 'QUALITY_MANAGER_RISK_MANAGEMENT_GROUP',
        dropdownKey: 'qualityManagerRiskManagement',
        children: [
          {
            label: 'AP Preventive Actions',
            icon: 'bi bi-shield-x',
            routerLink: '/app/quality-management/risk-management/ap-preventive-actions',
            accessKey: 'QUALITY_MANAGER_RISK_MANAGEMENT'
          },
          {
            label: 'CAP Corrective Actions',
            icon: 'bi bi-brush',
            routerLink: '/app/quality-management/risk-management/cap-corrective-actions',
            accessKey: 'QUALITY_MANAGER_RISK_MANAGEMENT'
          },
          {
            label: 'Meeting Minutes',
            icon: 'bi bi-list-task section-semicompleted',
            routerLink: '/app/quality-management/risk-management/meeting-minutes',
            accessKey: 'QUALITY_MANAGER_MEETING_MINUTES'
          },
          {
            label: 'Risk Management',
            icon: 'bi bi-list-task section-semicompleted',
            routerLink: '/app/quality-management/risk-management',
            accessKey: 'QUALITY_MANAGER_RISKS'
          },
        ]
      },
      {
        label: 'AP Preventive Actions',
        icon: 'bi bi-shield-x',
        routerLink: '/app/quality-management/ap-preventive-actions',
        accessKey: 'QUALITY_MANAGER_AP_PREVENTIVE_ACTIONS'
      },
      {
        label: 'CAP Corrective Actions',
        icon: 'bi bi-brush',
        routerLink: '/app/quality-management/cap-corrective-actions',
        accessKey: 'QUALITY_MANAGER_CAP_CORRECTIVE_ACTIONS'
      },
      {
        label: 'List Inspector/Tester',
        icon: 'bi bi-list-task',
        routerLink: '/app/quality-management/inspector-tester',
        accessKey: 'QUALITY_MANAGER_INSPECTOR_TESTER'
      },
      {
        label: 'Monitoring Jurisd. Changes',
        icon: 'bi bi-display',
        routerLink: '/app/quality-management/monitoring-jurisd-changes',
        accessKey: 'QUALITY_MANAGER_MONITORING_JURISD_CHANGES'
      },
      {
        label: 'Activities Planning',
        icon: 'bi bi-calendar-date section-semicompleted',
        routerLink: '/app/quality-management/activities-planning',
        accessKey: 'QUALITY_MANAGER_ACTIVITIES_PLANNING'
      },
      {
        label: 'List of Company Docs',
        icon: 'bi bi-list-task section-semicompleted',
        routerLink: '/app/quality-management/company-documents',
        accessKey: 'QUALITY_MANAGER_COMPANY_DOCS'
      },
      {
        label: 'Customer Survey',
        icon: 'bi bi-star',
        routerLink: '/app/quality-management/customer-survey',
        accessKey: 'QUALITY_MANAGER_CUSTOMER_SURVEY'
      },
      {
        label: 'Internal Audit',
        icon: 'bi bi-journal-bookmark',
        routerLink: '/app/quality-management/internal-audit',
        accessKey: 'QUALITY_MANAGER_INTERNAL_AUDIT'
      },
      {
        label: 'Settings',
        icon: 'bi bi-tools section-semicompleted',
        accessKey: 'QUALITY_MANAGER_SETTINGS_GROUP',
        dropdownKey: 'qualityManagerSettings',
        children: [
          {
            label: 'Risk Time Frame',
            icon: 'bi bi-list-task section-semicompleted',
            routerLink: '/app/quality-management/settings/risk-time-frame',
            accessKey: 'QUALITY_MANAGER_SETTINGS_RISK_TIME_FRAME'
          },
          {
            label: 'Quality Document Group',
            icon: 'bi bi-list-task',
            routerLink: '/app/quality-management/settings/quality-document-group',
            accessKey: 'QUALITY_MANAGER_SETTINGS'
          }
        ]
      },
      {
        label: 'File Manager',
        icon: 'bi bi-folder2-open',
        routerLink: '/app/quality-management/file-manager',
        accessKey: 'QUALITY_MANAGER_FILE_MANAGER'
      }
    ]
  },
  {
    header: 'MARKETING',
    items: [
      {
        label: 'Reports',
        icon: 'bi bi-file-earmark-text',
        routerLink: '/app/marketing/reports',
        accessKey: 'MARKETING_REPORTS'
      },
      {
        label: 'Campaigns List',
        icon: 'bi bi-list-task',
        routerLink: '/app/marketing/campaigns',
        accessKey: 'MARKETING_CAMPAIGNS'
      },
      {
        label: 'History',
        icon: 'bi bi-clock-history',
        routerLink: '/app/marketing/history',
        accessKey: 'MARKETING_HISTORY'
      },
      {
        label: 'Settings',
        icon: 'bi bi-tools',
        routerLink: '/app/marketing/settings',
        accessKey: 'MARKETING_SETTINGS'
      },
      {
        label: 'File Manager',
        icon: 'bi bi-folder2-open',
        routerLink: '/app/marketing/file-manager',
        accessKey: 'MARKETING_FILE_MANAGER'
      }
    ]
  },
  {
    header: 'OFFICE OPERATION',
    items: [
      {
        label: 'Mission & Travels',
        icon: 'bi bi-airplane',
        routerLink: '/app/office-operation/mission-travels',
        accessKey: 'OFFICE_OPERATION_MISSION_TRAVELS'
      },
      {
        label: 'Ranking Staff/Customers',
        icon: 'bi bi-graph-up-arrow',
        routerLink: '/app/office-operation/ranking-staff-customers',
        accessKey: 'OFFICE_OPERATION_RANKING_STAFF_CUSTOMERS'
      },
      {
        label: 'Travel Expences',
        icon: 'bi bi-piggy-bank',
        routerLink: '/app/office-operation/travel-expences',
        accessKey: 'OFFICE_OPERATION_TRAVEL_EXPENCES'
      }
    ]
  },
  {
    header: 'GENERAL SETTINGS',
    items: [
      {
        label: 'Category List',
        icon: 'bi bi-list-task section-completed',
        routerLink: '/app/general-settings/categories',
        accessKey: 'GS_CATEGORY'
      },
      {
        label: 'Regulation list',
        icon: 'bi bi-list-task section-completed',
        routerLink: '/app/general-settings/regulations',
        accessKey: 'GS_REGULATION'
      },
      {
        label: 'Law list',
        icon: 'bi bi-list-task section-completed',
        routerLink: '/app/general-settings/laws',
        accessKey: 'GS_LAW'
      },
      {
        label: 'Jurisdictions',
        icon: 'bi bi-list-task section-completed',
        routerLink: '/app/general-settings/jurisdictions',
        accessKey: 'GS_JURISDICTION'
      },
      {
        label: 'Items',
        icon: 'bi bi-list-task section-completed',
        routerLink: '/app/general-settings/items',
        accessKey: 'GS_ITEMS'
      },
      {
        label: 'Item types',
        icon: 'bi bi-list-task section-completed',
        routerLink: '/app/general-settings/item-types',
        accessKey: 'GS_ITEM_TYPE'
      },
      {
        label: 'Item products',
        icon: 'bi bi-list-task section-completed',
        routerLink: '/app/general-settings/item-products',
        accessKey: 'GS_ITEM_PRODUCTS'
      },
      {
        label: 'Users',
        icon: 'bi bi-list-task section-completed',
        routerLink: '/app/general-settings/users',
        accessKey: 'GS_USERS'
      },
      {
        label: 'Companies List',
        icon: 'bi bi-list-task section-completed',
        routerLink: '/app/general-settings/companies',
        accessKey: 'GS_COMPANIES'
      },      
      {
        label: 'Departments',
        icon: 'bi bi-list-task',
        routerLink: '/app/general-settings/departments',
        accessKey: 'GS_DEPARTMENTS'
      },
      {
        label: 'Access Control List',
        icon: 'bi bi-list-task',
        routerLink: '/app/general-settings/access-control-list',
        accessKey: 'GS_ACCESS_CONTROL_LIST'
      }
    ]
  },
];
